import * as React from 'react';
import { ICellRendererParams } from '@ag-grid-community/core';
import { StyleDetailsPopoverProps } from 'src/components/AssortmentStyleDetailsPopover/AssortmentStyleDetailsPopover';
import { IconCellRendererStyles } from 'src/pages/AssortmentBuild/StyleEdit/StyleEditSection/Renderers/IconCellRenderer';
import { classes, style } from 'typestyle';
import { ConfigurableGridConfigItem } from '../ConfigurableGrid/ConfigurableGrid.types';
import Renderer, { colorsFromClasses } from 'src/utils/Domain/Renderer';
import { isEmpty } from 'lodash';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { pickDataByMask } from 'src/utils/Component/AgGrid/AgConfigParse';
import { LinkConfirmationModal } from '../LinkConfirmationModal/LinkConfirmationModal';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';

// overriding the text centering here, as it likely isn't needed
export const IconPopoverStyleOverride = style({
  textAlign: 'left',
  $nest: {
    '.icon-with-popover-trigger': {
      marginRight: 15,
    },
  },
});
/** for navigable: false */
const disabledLinkStyle = style({
  opacity: 0.4,
  cursor: 'not-allowed',
  pointerEvents: 'none',
});
export interface Params
  extends ICellRendererParams,
    Pick<StyleDetailsPopoverProps, 'onItemClicked'>,
    Pick<ConfigurableGridConfigItem, 'rendererParams' | 'classes'> {
  dataQa?: string;
  icon: string;
  /**
   * when false, the link is visually disabled and click is suppressed
   * its computed from rendererParams.navigableByCalc
   * defaults to true when absent
   */
  navigable?: boolean;
}

interface State {
  modalOpen: boolean;
}

export default class IconWithPopoverTrigger extends React.Component<Params, State> {
  constructor(props: Params) {
    super(props);
    this.state = {
      modalOpen: false,
    };
  }

  handleClick = () => {
    const { data, onItemClicked, navigable = true } = this.props;

    if (!navigable) return;

    if (onItemClicked) {
      onItemClicked(data);
    }
  };

  shouldShowModal = () => {
    const { rendererParams } = this.props;
    return rendererParams?.confirmationModal == null;
  };

  renderOptionalModal = () => {
    const { rendererParams } = this.props;
    if (rendererParams?.confirmationModal == null) {
      return <></>;
    }
    return (
      <ConfirmationModal
        isLoading={false}
        isOpen={this.state.modalOpen}
        descriptionText={rendererParams.confirmationModal.description}
        onCancel={() => this.setState({ modalOpen: false })}
        onConfirm={() => {
          this.setState({ modalOpen: false });
          this.handleClick();
        }}
      />
    );
  };

  render() {
    const { value, icon, dataQa, rendererParams, navigable = true } = this.props;
    let renderedValue: JSX.Element | string = value;
    let fontColor: string | undefined;

    let iconClass = `fa fa-${icon}`;
    if (iconClass.startsWith('far') || iconClass.startsWith('fas') || iconClass.startsWith('fa')) {
      iconClass = icon;
    }

    if (rendererParams?.innerRenderer) {
      // "fake" colInfo to pass to the renderer
      const fakeColInfo = {
        renderer: rendererParams.innerRenderer,
        classes: this.props.classes || [],
      } as unknown as ConfigurableGridConfigItem;

      renderedValue = Renderer.renderJustValue(value, fakeColInfo);
    } else if (rendererParams?.mask) {
      const renderValues = pickDataByMask(
        this.props.data,
        this.props.rendererParams,
        this.props.api,
        this.props.node,
      ) as BasicPivotItem;

      renderedValue = Renderer.styledTemplate(renderValues, rendererParams.mask, this.props.classes || []);
    }

    const matchedColor = colorsFromClasses(this.props.classes || []);
    if (!isEmpty(matchedColor)) {
      fontColor = matchedColor.map((str) => str.replace('color_', ''))[0];
    }

    return (
      <div
        data-qa={dataQa}
        className={classes(
          IconCellRendererStyles.container,
          IconPopoverStyleOverride,
          !navigable ? disabledLinkStyle : undefined,
        )}
        style={{ color: fontColor ?? 'inherit' }}
        onClick={() => {
          if (this.shouldShowModal()) {
            this.handleClick();
          } else if (!this.state.modalOpen) {
            this.setState({ modalOpen: true });
          }
        }}
      >
        {!isEmpty(icon) ? <i className={`icon-with-popover-trigger ${iconClass}`} /> : null}
        {renderedValue ? renderedValue : undefined}
        {this.renderOptionalModal()}
      </div>
    );
  }
}
